import { useState } from "react";
import {
  Card,
  BlockStack,
  Text,
  Button,
  InlineStack,
  Banner,
  Box,
  DropZone,
  Thumbnail,
  InlineGrid,
  SkeletonThumbnail,
  Divider,
  Bleed,
  SortableList,
  SortableResourceItem,
} from "@medentic/polaris-react";
import {
  DeleteIcon,
  PlusIcon,
  EditIcon,
} from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { Venue, MediaItem, ImageDetailType } from "@features/events/types";
import { WebsiteField } from "@shared/ui";
import { AddMediaModal } from "@shared/ui";
import { MediaEntityType } from "@shared/ui";

interface MediaCardProps {
  venueData: Partial<Venue>;
  isNewVenue: boolean;
  isUploadingThumbnail: boolean;
  hcc: string | undefined;
  venueId: string | undefined;
  onDataChange: (updates: Partial<Venue>) => void;
  onRemoveMedia: (id: string) => void;
  onReorderMedia: (reorderedItems: MediaItem[]) => void;
  onDropMedia: (reorderedItems: MediaItem[]) => void;
  onVideoThumbnailUpload: (files: File[]) => void;
}

interface MediaItemProps {
  item: MediaItem;
  onRemove: (itemId: string) => void;
  onEdit: (item: MediaItem) => void;
  language: string;
}

const MediaItemComponent: React.FC<MediaItemProps> = ({
  item,
  onRemove,
  onEdit,
  language,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getLocalizedName = (
    name: Record<string, string | undefined>
  ): string => {
    return name[language] || Object.values(name).filter(Boolean)?.[0] || "";
  };

  return (
    <SortableResourceItem
      accessibilityLabel={`Edit ${getLocalizedName(item.name) || "media item"}`}
      dragHandleAccessibilityLabel={`Reorder ${getLocalizedName(item.name) || "media item"}`}
    >
      <div
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <InlineGrid columns={["twoThirds", "oneThird"]}>
          <InlineStack gap="200" align="start" blockAlign="center" wrap={false}>
            {item.source && (item.source as ImageDetailType[]).length > 0 ? (
              <img
                src={(item.source as ImageDetailType[])[0]?.src || ""}
                alt={getLocalizedName(item.name)}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  objectFit: "cover",
                }}
              />
            ) : (
              <SkeletonThumbnail size="small" />
            )}

            <BlockStack gap="100">
              <Text as="span" variant="bodyMd">
                {getLocalizedName(item.name)}
              </Text>
            </BlockStack>
          </InlineStack>

          <InlineStack gap="200" align="space-between" blockAlign="center">
            <Box></Box>
            <Box opacity={isHovered ? "1" : "0"} paddingInlineEnd="100">
              <InlineStack gap="200" blockAlign="center" align="end">
                <Button
                  icon={EditIcon}
                  variant="tertiary"
                  onClick={() => onEdit(item)}
                />
                <Button
                  icon={DeleteIcon}
                  tone="critical"
                  variant="tertiary"
                  onClick={() => onRemove(getLocalizedName(item.name))}
                />
              </InlineStack>
            </Box>
          </InlineStack>
        </InlineGrid>
      </div>
    </SortableResourceItem>
  );
};

// Helper function to get YouTube thumbnail
const getYouTubeThumbnail = (url: string): string | null => {
  const match = url.match(/(?:\?v=|\/embed\/|\.be\/)([^\s&]+)/);
  return match ? `https://img.youtube.com/vi/${match[1]}/maxresdefault.jpg` : null;
};

// Helper to get localized media name
const getLocalizedName = (
  name: Record<string, string | undefined>,
  language: string
): string => {
  return name[language] || Object.values(name).filter(Boolean)?.[0] || "";
};

export default function MediaCard({
  venueData,
  isNewVenue,
  isUploadingThumbnail,
  hcc,
  venueId,
  onDataChange,
  onRemoveMedia,
  onReorderMedia,
  onDropMedia,
  onVideoThumbnailUpload,
}: MediaCardProps) {
  const { t, i18n } = useTranslation();
  const [isMediaModalOpen, setIsMediaModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MediaItem | null>(null);

  // Handle adding media from modal
  const handleAddMedia = (mediaArray: MediaItem[]) => {
    const newMediaItems = mediaArray
      .filter(media => media.type === "img" && typeof media.source !== "string")
      .map(media => ({
        type: "img" as const,
        name: media.name,
        source: media.source,
      }));
    
    if (newMediaItems.length > 0) {
      onDataChange({
        media: [...(venueData.media || []), ...newMediaItems],
      });
    }
    setIsMediaModalOpen(false);
    setEditingItem(null);
  };

  // Handle editing media from modal
  const handleEditMedia = (updatedMedia: MediaItem) => {
    if (editingItem && venueData.media) {
      const updatedMediaList = venueData.media.map((item) =>
        getLocalizedName(item.name, i18n.language) === 
        getLocalizedName(editingItem.name as Record<string, string | undefined>, i18n.language)
          ? updatedMedia.type === "img" && typeof updatedMedia.source !== "string"
            ? { type: "img" as const, name: updatedMedia.name, source: updatedMedia.source }
            : item
          : item
      );
      onDataChange({ media: updatedMediaList });
    }
    setIsMediaModalOpen(false);
    setEditingItem(null);
  };

  // Handle edit click
  const handleEditClick = (item: MediaItem) => {
    const galleryItem: MediaItem = {
      type: item.type,
      name: item.name,
      source: item.source,
    };
    setEditingItem(galleryItem);
    setIsMediaModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setIsMediaModalOpen(false);
    setEditingItem(null);
  };

  // Render media item
  const renderMediaItem = (
    item: MediaItem
  ) => (
    <MediaItemComponent
      item={item}
      onRemove={onRemoveMedia}
      onEdit={handleEditClick}
      language={i18n.language}
    />
  );

  return (
    <>
      <Card>
        <BlockStack gap="400">
          <Text as="h2" variant="headingMd">
            {t("venues.details.section.media")}
          </Text>

        {isNewVenue && (
          <Banner tone="info">
            {t("events.common.media_item_can_be_added_after_save")}
          </Banner>
        )}

        {/* Media Gallery */}
        <BlockStack gap="300">
          <Text as="h3" variant="bodyMd">
            {t("eventDetails.photosVideos.mediaGallery")}
          </Text>

          {!venueData.media || venueData.media.length === 0 ? (
            <Box padding="600" borderRadius="200">
              <InlineStack align="center">
                <Text as="p" variant="bodyMd" tone="subdued">
                  {t("instructorDetails.profileImageAndMedia.noMediaAdded")}
                </Text>
              </InlineStack>
            </Box>
          ) : (
            <Box
              borderWidth="025"
              borderColor="border"
              borderRadius="200"
              background="bg-surface"
            >
              <SortableList
                items={venueData.media}
                onChange={onReorderMedia}
                onReorderEnd={onDropMedia}
                idForItem={(item) =>
                  `${getLocalizedName(item.name, i18n.language)}`
                }
                renderItem={(item) => renderMediaItem(item)}
                showDividers={true}
              />
            </Box>
          )}

          <InlineStack align="center">
            <Button
              icon={PlusIcon}
              variant="primary"
              onClick={() => {
                setEditingItem(null);
                setIsMediaModalOpen(true);
              }}
              disabled={isNewVenue}
            >
              {t("addMediaModal.addImage")}
            </Button>
          </InlineStack>
        </BlockStack>

        <Bleed marginInline="400">
          <Divider />
        </Bleed>
        
        {/* Video Section */}
        <BlockStack gap="200">
            <Text as="h3" variant="bodyMd">
            {t("venues.details.field.video.label")}
          </Text>
          <WebsiteField
            label=""
            value={venueData.video || ""}
            onChange={(value) => onDataChange({ video: value })}
            placeholder={t("common.placeholders.youtubeUrl")}
            helpText={t("venues.details.field.video.help_text")}
            requireHttps={false}
          />

          {venueData.video && (
            <Box>
              <BlockStack gap="200">
                <Text as="h3" variant="bodyMd">
                  {t("venues.details.field.video_thumbnail.label")}
                </Text>
                {venueData.videoImage && venueData.videoImage.length > 0 ? (
                  <Box>
                    <Thumbnail
                      source={venueData.videoImage[0].src}
                      alt={t("venues.details.field.video_thumbnail.label")}
                      size="large"
                    />
                    <Box paddingBlockStart="200">
                      <DropZone
                        accept="image/*"
                        type="image"
                        onDrop={onVideoThumbnailUpload}
                        disabled={isUploadingThumbnail || isNewVenue}
                      >
                        <DropZone.FileUpload />
                      </DropZone>
                    </Box>
                  </Box>
                ) : (
                  <Box>
                    {getYouTubeThumbnail(venueData.video) && (
                      <Thumbnail
                        source={getYouTubeThumbnail(venueData.video)!}
                        alt={t("venues.details.field.video_thumbnail.defaultAlt")}
                        size="large"
                      />
                    )}
                    <Box paddingBlockStart="200">
                      <DropZone
                        accept="image/*"
                        type="image"
                        onDrop={onVideoThumbnailUpload}
                        disabled={isUploadingThumbnail || isNewVenue}
                      >
                        <DropZone.FileUpload />
                      </DropZone>
                    </Box>
                  </Box>
                )}
                <Text as="p" variant="bodySm" tone="subdued">
                  {t("venues.details.field.video_thumbnail.help_text")}
                </Text>
              </BlockStack>
            </Box>
          )}
        </BlockStack>
      </BlockStack>
    </Card>

    {/* Add Media Modal */}
    <AddMediaModal
      isOpen={isMediaModalOpen}
      onClose={handleCloseModal}
      onAddMedia={handleAddMedia}
      onEditMedia={handleEditMedia}
      editingItem={editingItem}
      type="img"
      hcc={hcc}
      entityType={MediaEntityType.VENUE}
      entityId={venueId}
      venueData={venueData}
    />
    </>
  );
}
